import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface MissionImpact {
  id: string;
  mission_id: string;
  impact_type_name: string;
  quantity_per_completion: number;
  unit: string;
  description: string;
}

interface UserImpactHistory {
  id: string;
  user_id: string;
  mission_id: string;
  impact_type_name: string;
  quantity: number;
  unit: string;
  created_at: string;
  missions?: {
    title: string;
    category: string;
  };
}

interface ImpactSummary {
  co2_saved: number;
  waste_reduced: number;
  trees_planted: number;
  water_saved: number;
  energy_saved: number;
  total_actions: number;
  missions_completed: number;
}

// Mock data for when Supabase is not configured
const mockMissionImpacts: MissionImpact[] = [
  {
    id: 'impact-1',
    mission_id: 'mission-1',
    impact_type_name: 'waste_reduced',
    quantity_per_completion: 0.1,
    unit: 'kg',
    description: 'Plastic bag waste prevented'
  },
  {
    id: 'impact-2',
    mission_id: 'mission-6',
    impact_type_name: 'waste_reduced',
    quantity_per_completion: 0.5,
    unit: 'kg',
    description: 'Plastic waste diverted from landfills'
  },
  {
    id: 'impact-3',
    mission_id: 'mission-3',
    impact_type_name: 'co2_saved',
    quantity_per_completion: 2.5,
    unit: 'kg',
    description: 'CO2 emissions avoided by not driving'
  },
  {
    id: 'impact-4',
    mission_id: 'mission-4',
    impact_type_name: 'co2_saved',
    quantity_per_completion: 1.8,
    unit: 'kg',
    description: 'CO2 emissions saved vs meat-based meal'
  },
  {
    id: 'impact-5',
    mission_id: 'mission-4',
    impact_type_name: 'water_saved',
    quantity_per_completion: 20,
    unit: 'L',
    description: 'Water saved vs meat production'
  }
];

export function useMissionImpacts() {
  const [missionImpacts, setMissionImpacts] = useState<MissionImpact[]>([]);
  const [userImpactHistory, setUserImpactHistory] = useState<UserImpactHistory[]>([]);
  const [impactSummary, setImpactSummary] = useState<ImpactSummary>({
    co2_saved: 0,
    waste_reduced: 0,
    trees_planted: 0,
    water_saved: 0,
    energy_saved: 0,
    total_actions: 0,
    missions_completed: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMissionImpacts();
  }, []);

  const fetchMissionImpacts = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using mock mission impacts data');
        setMissionImpacts(mockMissionImpacts);
        setLoading(false);
        return;
      }

      console.log('Fetching mission impacts from database...');
      
      const { data, error } = await supabase
        .from('mission_impacts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching mission impacts:', error);
        setMissionImpacts(mockMissionImpacts);
        return;
      }

      setMissionImpacts(data || []);
    } catch (error) {
      console.error('Error fetching mission impacts:', error);
      setMissionImpacts(mockMissionImpacts);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserImpactHistory = async (userId: string) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Using mock user impact history');
        return;
      }

      console.log('Fetching user impact history...');
      
      const { data, error } = await supabase
        .from('user_impact_history')
        .select(`
          *,
          missions:mission_id (title, category)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user impact history:', error);
        return;
      }

      setUserImpactHistory(data || []);
      
      // Calculate summary
      const summary = (data || []).reduce((acc, item) => {
        acc[item.impact_type_name as keyof ImpactSummary] = 
          (acc[item.impact_type_name as keyof ImpactSummary] as number || 0) + item.quantity;
        acc.total_actions++;
        return acc;
      }, {
        co2_saved: 0,
        waste_reduced: 0,
        trees_planted: 0,
        water_saved: 0,
        energy_saved: 0,
        total_actions: 0,
        missions_completed: new Set(data?.map(d => d.mission_id)).size || 0
      });

      setImpactSummary(summary);
    } catch (error) {
      console.error('Error fetching user impact history:', error);
    }
  };

  const getMissionImpacts = (missionId: string): MissionImpact[] => {
    return missionImpacts.filter(impact => impact.mission_id === missionId);
  };

  const addMissionImpact = async (missionImpact: Omit<MissionImpact, 'id'>) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock add mission impact:', missionImpact);
        const newImpact = {
          ...missionImpact,
          id: `impact-${Date.now()}`
        };
        setMissionImpacts(prev => [...prev, newImpact]);
        return { error: null };
      }

      const { error } = await supabase
        .from('mission_impacts')
        .insert(missionImpact);

      if (error) {
        console.error('Error adding mission impact:', error);
        return { error };
      }

      await fetchMissionImpacts();
      return { error: null };
    } catch (error) {
      console.error('Error adding mission impact:', error);
      return { error };
    }
  };

  const updateMissionImpact = async (id: string, updates: Partial<MissionImpact>) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock update mission impact:', id, updates);
        setMissionImpacts(prev => prev.map(impact => 
          impact.id === id ? { ...impact, ...updates } : impact
        ));
        return { error: null };
      }

      const { error } = await supabase
        .from('mission_impacts')
        .update(updates)
        .eq('id', id);

      if (error) {
        console.error('Error updating mission impact:', error);
        return { error };
      }

      await fetchMissionImpacts();
      return { error: null };
    } catch (error) {
      console.error('Error updating mission impact:', error);
      return { error };
    }
  };

  const deleteMissionImpact = async (id: string) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock delete mission impact:', id);
        setMissionImpacts(prev => prev.filter(impact => impact.id !== id));
        return { error: null };
      }

      const { error } = await supabase
        .from('mission_impacts')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting mission impact:', error);
        return { error };
      }

      await fetchMissionImpacts();
      return { error: null };
    } catch (error) {
      console.error('Error deleting mission impact:', error);
      return { error };
    }
  };

  return {
    missionImpacts,
    userImpactHistory,
    impactSummary,
    loading,
    getMissionImpacts,
    addMissionImpact,
    updateMissionImpact,
    deleteMissionImpact,
    fetchUserImpactHistory,
    refetch: fetchMissionImpacts
  };
}